// src/utils/auth.js
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from "firebase/auth";
import bs58 from "bs58";
import { anonymousAuthReady, auth } from "../firebaseConfig";
import { reportWalletLogin, trackUsageEvent, usageAttempt, usageFailureCode } from './usageAnalytics';


const functions = getFunctions(getApp());
const getNonce = httpsCallable(functions, "getNonce");


const openPublishSession = httpsCallable(functions, "openPublishSession");
let firebaseSignInQueue = Promise.resolve();

const SOLANA_SIGN_MESSAGE_FEATURE = 'solana:signMessage';
const LEGACY_SIGN_MESSAGE_FEATURE = 'standard:signMessage';


function buildPublishMessage({ walletBase58, nonce }) {
    const nowIso = new Date().toISOString();
    return [
        'MuultiSpace Publish Authorization',
        `Wallet: ${walletBase58}`,
        `Nonce: ${nonce}`,
        `Time: ${nowIso}`
    ].join('\n');
}

function getSelectedWalletAdapter(wallet) {
    return wallet?.wallet?.adapter || wallet?.adapter || wallet;
}

function getStandardSignMessageFeature(wallet) {
    const adapter = getSelectedWalletAdapter(wallet);
    const features =
        adapter?.wallet?.features ||
        adapter?.features ||
        wallet?.wallet?.features ||
        wallet?.features ||
        {};
    return (
        features[SOLANA_SIGN_MESSAGE_FEATURE] ||
        features[LEGACY_SIGN_MESSAGE_FEATURE] ||
        null
    );
}

function getStandardSignMessageAccount(wallet, walletBase58) {
    const adapter = getSelectedWalletAdapter(wallet);
    const accounts =
        adapter?.wallet?.accounts ||
        adapter?.accounts ||
        wallet?.wallet?.accounts ||
        wallet?.accounts ||
        [];

    if (!Array.isArray(accounts) || accounts.length === 0) return null;

    return accounts.find((account) => {
        const address = String(account?.address || account?.publicKey?.toBase58?.() || '').trim();
        return address === walletBase58;
    }) || accounts[0];
}

// Robust feature detection across adapters
export function walletSupportsSignMessage(wallet) {
    if (!wallet) return false;
    if (typeof wallet.signMessage === 'function') return true;
    const adapter = getSelectedWalletAdapter(wallet);
    if (adapter && adapter !== wallet && typeof adapter.signMessage === 'function') return true;
    if (typeof getStandardSignMessageFeature(wallet)?.signMessage === 'function') return true;
    return false;
}

export async function openPublishSessionForWallet(wallet, region = 'us-central1', options = {}) {
    const attempt = usageAttempt('wallet');
    void trackUsageEvent('wallet_login_attempted', attempt);
    try {
        return await authenticateWalletSession(wallet, region, options);
    } catch (error) {
        void trackUsageEvent('wallet_login_failed', { ...attempt, failureCode: usageFailureCode(error) });
        throw error;
    }
}

async function authenticateWalletSession(wallet, region, { isCurrent = () => true } = {}) {
    if (!wallet?.publicKey) throw new Error('Connect your wallet first');
    if (!walletSupportsSignMessage(wallet)) {
        const adapterName = getSelectedWalletAdapter(wallet)?.name || 'Selected wallet';
        throw new Error(`${adapterName} does not expose message signing (signMessage). Reconnect it or select a wallet that supports message signing.`);
    }

        const walletBase58 =
                (typeof wallet.publicKey === "string"
                    ? wallet.publicKey
                        : wallet.publicKey?.toBase58?.() || ""
                    )
                .trim()
            // protect against accidental display formatting leaking in
            .replace(/\u2026/g, ""); // ellipsis …

        // Strict base58 (Solana pubkeys are typically 32-44 chars)
            const BASE58_RE = /^[1-9A-HJ-NP-Za-km-z]+$/;
        if (!walletBase58 || !BASE58_RE.test(walletBase58)) {
                console.error("Bad walletBase58:", JSON.stringify(walletBase58), "len:", walletBase58?.length);
                throw new Error("Wallet publicKey is not valid base58 (check for … or hidden characters)");
            }

    const adapter = wallet.wallet?.adapter || wallet.adapter || wallet;
    const assertCurrentWallet = () => {
        const currentKey = typeof adapter.publicKey === 'string'
            ? adapter.publicKey : adapter.publicKey?.toBase58?.();
        if (!isCurrent() || currentKey !== walletBase58 ||
            (adapter.connected === false && !adapter.connecting)) {
            throw new Error('Wallet changed while authentication was in progress.');
        }
    };
    assertCurrentWallet();

    // 1) Fetch nonce
    let nonceData;
    try {
        const res = await getNonce({ publicKey: walletBase58 });
        nonceData = res.data;
    } catch (e) {
        console.error("❌ getNonce failed");
        console.error("code:", e.code);
        console.error("message:", e.message);
        console.error("details:", e.details);
        console.error("publicKey sent:", walletBase58);
        throw e;
    }

    assertCurrentWallet();
    const nonce = nonceData?.nonce;
    if (!nonce) throw new Error('Server did not return a nonce');

    // 2) Build and sign message
    const message = buildPublishMessage({walletBase58, nonce});
    const msgBytes = new TextEncoder().encode(message);

    // Prefer wallet-adapter API
    let signature;
    if (typeof wallet.signMessage === 'function') {
        signature = await wallet.signMessage(msgBytes);
    } else if (typeof getSelectedWalletAdapter(wallet)?.signMessage === 'function') {
        signature = await getSelectedWalletAdapter(wallet).signMessage(msgBytes);
    } else if (getStandardSignMessageFeature(wallet)?.signMessage) {
        const feature = getStandardSignMessageFeature(wallet);
        const account = getStandardSignMessageAccount(wallet, walletBase58);
        if (!account) {
            throw new Error('Selected wallet did not expose an account for message signing.');
        }
        const res = await feature.signMessage({ account, message: msgBytes });
        signature = res; // normalize below
    } else {
        throw new Error('No message-signing method available on the connected wallet.');
    }

// Normalize signature bytes across wallet-adapter / standard wallets
    const sigBytes =
        signature instanceof Uint8Array
            ? signature
            : signature?.signature instanceof Uint8Array
            ? signature.signature
            : Array.isArray(signature) && signature[0]?.signature instanceof Uint8Array
                ? signature[0].signature
                : signature instanceof ArrayBuffer
                    ? new Uint8Array(signature)
                    : ArrayBuffer.isView(signature)
                        ? new Uint8Array(signature.buffer)
                        : null;

    if (!sigBytes) {
        console.error("Unexpected signature shape:", signature);
        throw new Error("Unexpected signature type from wallet signMessage");
    }

    assertCurrentWallet();
    const signatureBase58 = bs58.encode(sigBytes).trim();
    const publicKeyBase58 = String(walletBase58).trim();



    console.log("publicKey len:", publicKeyBase58.length, "valid:", BASE58_RE.test(publicKeyBase58));
    console.log("signature len:", signatureBase58.length, "valid:", BASE58_RE.test(signatureBase58));
    console.log("sigBytes length:", sigBytes.length); // should be 64 for ed25519

    if (!BASE58_RE.test(publicKeyBase58)) throw new Error("Public key is not valid base58");
    if (!BASE58_RE.test(signatureBase58)) throw new Error("Signature is not valid base58");

// ✅ Call openPublishSession ONCE, inside try/catch
    let data;
    try {
        const res = await openPublishSession({
            publicKey: publicKeyBase58,
            signature: signatureBase58,
            message,
        });
        data = res.data;
    } catch (e) {
        console.error("❌ openPublishSession failed");
        console.error("code:", e.code);
        console.error("message:", e.message);
        console.error("details:", e.details);
        console.error("payload:", {
            publicKey: publicKeyBase58,
            signature: signatureBase58.slice(0, 16) + "...",
            message,
        });
        throw e;
    }

    const customToken = data?.customToken;
    if (!customToken) throw new Error("Server did not return a Firebase custom token");

// ✅ Sign into Firebase as uid = wallet pubkey
    await anonymousAuthReady;
    // Serialize Firebase identity changes and recheck immediately before sign-in.
    // A late response from a disconnected wallet cannot overwrite the next login.
    const signIn = firebaseSignInQueue.then(async () => {
        assertCurrentWallet();
        await signInWithCustomToken(auth, customToken);
        assertCurrentWallet();
    });
    firebaseSignInQueue = signIn.catch(() => {});
    await signIn;
    void reportWalletLogin(walletBase58);

    return data;


}
